import { indexedDBJSONStorage } from './indexedDBStorage';
import { getUrlCacheKey } from '../services/queryParams';

const pick = (source, fields) =>
  Object.fromEntries(fields.filter((field) => source && field in source).map((field) => [field, source[field]]));

// Only fields that hold something: a field still at its empty initial value says nothing
// about the work done in memory and must not overwrite what the record holds.
const pickWorkedOn = (source, fields) =>
  Object.fromEntries(Object.entries(pick(source, fields)).filter(([, value]) => value !== null && value !== undefined && value !== false));

// Tracks which ticket a store's in-memory state belongs to and brings it in line with the
// URL's ticket. `follow(applyTicket)` returns null when nothing changed, so callers only
// `await` a real switch. Callers that arrive mid-switch share one promise, and it loops
// until the applied ticket matches the URL, so a rapid A -> B -> C never lets a caller
// continue while the store still holds B. When persist reports that reading a record
// failed (zustand swallows storage errors and only tells `onHydrated`), the ticket is still
// recorded (so the loop ends) but the state is not `isLoaded`: writes stay blocked, so the
// empty state cannot overwrite the unread record, and the next `follow` retries.
export const createTicketFollower = () => {
  let appliedKey = getUrlCacheKey();
  let isLoaded = true;
  let generation = 0;
  let pending = null;
  // persist starts reading the initial ticket's record on its own when the store is
  // created. A switch must not start until that read has landed, or it could land last
  // and replace the new ticket's state.
  let resolveInitialHydration;
  const initialHydration = new Promise((resolve) => { resolveInitialHydration = resolve; });
  let lastHydrationFailed = false;

  const settle = async (applyTicket) => {
    await initialHydration;
    do {
      const ticketKey = getUrlCacheKey();
      // Same ticket again means a retry of a failed load, not a switch: in-flight
      // responses for this ticket stay valid and the caller must keep its in-memory edits.
      const isRetry = ticketKey === appliedKey;
      if (!isRetry) generation += 1;
      let afterLoaded;
      try {
        afterLoaded = await applyTicket({ isRetry });
      } finally {
        isLoaded = !lastHydrationFailed;
        appliedKey = ticketKey;
      }
      // Work kept through a retry was set while writes were still blocked; once the load
      // is confirmed, the caller can save it now instead of on some later change.
      if (isRetry && isLoaded) afterLoaded?.();
    } while (getUrlCacheKey() !== appliedKey);
  };

  return {
    appliedKey: () => appliedKey,
    isLoaded: () => isLoaded,
    // For persist's onRehydrateStorage: `() => follower.onHydrated`. Called after every
    // hydration, with the error when the stored record could not be read.
    onHydrated: (_state, error) => {
      lastHydrationFailed = Boolean(error);
      if (error) {
        isLoaded = false;
        console.error('Failed to load stored state:', error);
      }
      resolveInitialHydration();
    },
    // Counts applied switches, so a ticket that was left and came back is told apart.
    generation: () => generation,
    follow: (applyTicket) => {
      if (!pending && getUrlCacheKey() === appliedKey && isLoaded) return null;
      if (!pending) {
        pending = settle(applyTicket).finally(() => { pending = null; });
      }
      return pending;
    },
  };
};

// Persistence for a store whose state belongs to one ticket (issueKey + token).
//
// zustand's persist captures its `name` once, when the module loads, but HomeTab
// switches tickets with history.pushState and no reload. Left alone, data fetched for
// ticket B would be saved under ticket A's record and lost on the next reload of B.
// So the record is derived from the current ticket on every call, and `followTicket`
// resets and reloads the store when the ticket changes.
//
// Only `persistedFields` are saved or restored. Transient fields (loading flags,
// polling handles) must survive a rehydrate untouched, and stale ones in older
// records are ignored.
export const createTicketScopedPersistence = ({ prefix, persistedFields, resetState }) => {
  const recordNameFor = (ticketKey) => `${prefix}${ticketKey}`;

  // The ticket the in-memory state was loaded for. Right after a switch the memory
  // still holds the previous ticket's data; it must never be written to the new
  // ticket's record.
  const follower = createTicketFollower();
  const initialTicketKey = follower.appliedKey();

  const storage = {
    // async so persist always receives a native promise and routes a rejection to onHydrated
    getItem: async () => indexedDBJSONStorage.getItem(recordNameFor(getUrlCacheKey())),
    setItem: async (_name, value) => {
      const ticketKey = getUrlCacheKey();
      if (ticketKey !== follower.appliedKey() || !follower.isLoaded()) return;
      try {
        await indexedDBJSONStorage.setItem(recordNameFor(ticketKey), value);
      } catch (error) {
        console.error('Failed to save state:', error);
      }
    },
    removeItem: () => indexedDBJSONStorage.removeItem(recordNameFor(getUrlCacheKey())),
  };

  // Call before reading or fetching the store's data. Returns null when the ticket is
  // unchanged, so the common path keeps its synchronous timing.
  const followTicket = (store) => follower.follow(async ({ isRetry }) => {
    // Clearing first means a ticket with no stored record starts empty instead of
    // showing the previous ticket's data. Not persisted: state still belongs to the old ticket.
    // A retry after a failed read keeps what was done in memory meanwhile, which is newer
    // than the record.
    const keptInMemory = isRetry ? pickWorkedOn(store.getState(), persistedFields) : null;
    if (!isRetry) store.setState(resetState);
    await store.persist.rehydrate();
    if (!keptInMemory) return undefined;
    store.setState(keptInMemory);
    return () => store.setState({}); // persists the merged state
  });

  // Remembers the ticket a request was made for. When the response arrives, the returned
  // function says whether the technician is still on that ticket; if not, the response
  // belongs to state that is no longer in memory and must be dropped rather than saved
  // under the ticket they switched to.
  const trackTicket = () => {
    const ticketKey = getUrlCacheKey();
    const startGeneration = follower.generation();
    // The generation catches a round trip (A -> B -> A) in which the state was reloaded
    // and may have newer local edits that the old response must not overwrite.
    return () => getUrlCacheKey() === ticketKey && follower.generation() === startGeneration;
  };

  return {
    followTicket,
    trackTicket,
    persistOptions: {
      name: recordNameFor(initialTicketKey),
      storage,
      onRehydrateStorage: () => follower.onHydrated,
      partialize: (state) => pick(state, persistedFields),
      merge: (persisted, current) => ({ ...current, ...pick(persisted, persistedFields) }),
    },
  };
};
